import { useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { Category } from '@/api/games';
import type { UpdatedGameCategory } from '@/helpers/game';
import { formatCategories } from '@/helpers/gameFiltration';
import { useUserWageringBonuses } from '@/hooks/bonuses';
import { useMatchingSections } from '@/modules/restrictions/hooks/useAccessSections';
import { useGetCommonStore } from '@/store';
import { useCollections } from './useCollections';

export const useCMSGameCategories = () => {
  const useCommonStore = useGetCommonStore();

  const { pendingUser, isAuthenticated, isAuthenticatedByCookie } =
    useCommonStore(
      useShallow((state) => ({
        pendingUser: state.pendingUser,
        isAuthenticated: state.isAuthenticated,
        isAuthenticatedByCookie: state.isAuthenticatedByCookie,
      }))
    );
  const shouldWaitForUser = pendingUser && isAuthenticatedByCookie;

  const { collections } = useCollections();
  const { hasWageringBonus } = useUserWageringBonuses();

  const data = useCommonStore.use.gameCategories();

  const formatted = useMemo(
    () =>
      data.length && collections.size && !shouldWaitForUser
        ? formatCategories(isAuthenticated, collections)(data)
        : [],
    [data, isAuthenticated, collections, shouldWaitForUser]
  );

  const filteredByGroups = useMatchingSections<UpdatedGameCategory>(formatted);

  const categories = useMemo(
    () =>
      filteredByGroups.filter(
        (category) => category.id !== Category.WAGERING || hasWageringBonus
      ),
    [filteredByGroups, hasWageringBonus]
  );

  return { categories };
};
